/**
 * Service frontend para workflow multi-etapa (0641 — T-5).
 *
 * Wrappers tipados sobre fetch (request helper) para los endpoints
 * `/api/v1/workflow/*` añadidos en 0641.
 */

import { request } from '../../shared/services/api';

export type ArtefactoTipo = 'ORGANIGRAMA' | 'DOC' | 'FLUJOGRAMA' | 'PROCESO';
export type AccionEtapa = 'REVISAR' | 'APROBAR' | 'NOTIFICAR';
export type ModoEtapa = 'TODOS' | 'PRIMERO';
export type CanalNotifWorkflow = 'IN_APP' | 'EMAIL' | 'TEAMS' | 'SLACK';

export const CANALES_NOTIF: CanalNotifWorkflow[] = ['IN_APP', 'EMAIL', 'TEAMS', 'SLACK'];

export interface TipoRolRef {
  id: string;
  codigo: string | null;
  nombre: string;
}

export interface PuestoRef {
  id: string;
  codigo: string | null;
  titulo: string;
}

export interface EtapaRol {
  id: string;
  tipoRol: TipoRolRef | null;
  puestoFiltro: PuestoRef | null;
}

export interface Etapa {
  id: string;
  orden: number;
  accion: AccionEtapa;
  modo: ModoEtapa;
  roles: EtapaRol[];
}

export interface ConfigEtapas {
  workflowConfigId: string | null;
  rolGobernanzaIniciador: string | null;
  rolGobernanzaPublicador: string | null;
  rolGobernanzaArchivador: string | null;
  requiereFirmaQES: boolean;
  etapas: Etapa[];
}

export interface ConfigPatch {
  rolGobernanzaIniciador?: string | null;
  rolGobernanzaPublicador?: string | null;
  rolGobernanzaArchivador?: string | null;
  requiereFirmaQES?: boolean;
}

/** Actualiza los campos de cabecera del WorkflowConfig (iniciador/publicador/archivador). */
export async function actualizarConfig(
  artefactoTipo: ArtefactoTipo,
  patch: ConfigPatch,
): Promise<unknown> {
  return request<{ data: unknown }>(`/workflow/config/${artefactoTipo}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

export interface EtapaActual {
  etapaId: string;
  orden: number;
  accion: string;
  modo: string;
  totalEtapas: number;
}

// =============================================================================
// READ
// =============================================================================

export async function fetchEtapasConfig(artefactoTipo: ArtefactoTipo): Promise<ConfigEtapas> {
  const r = await request<{ data: ConfigEtapas }>(`/workflow/config/${artefactoTipo}/etapas`);
  return r.data;
}

export async function fetchEtapaActual(
  artefactoTipo: ArtefactoTipo,
  artefactoId: string,
): Promise<EtapaActual | null> {
  const r = await request<{ data: EtapaActual | null }>(
    `/workflow/etapa-actual/${artefactoTipo}/${artefactoId}`,
  );
  return r.data;
}

// =============================================================================
// WRITE — admin only
// =============================================================================

export async function crearEtapa(
  artefactoTipo: ArtefactoTipo,
  accion: AccionEtapa,
  modo: ModoEtapa = 'TODOS',
): Promise<Etapa> {
  const r = await request<{ data: Etapa }>(`/workflow/config/${artefactoTipo}/etapas`, {
    method: 'POST',
    body: JSON.stringify({ accion, modo }),
  });
  return r.data;
}

export async function actualizarEtapa(
  etapaId: string,
  patch: { accion?: AccionEtapa; modo?: ModoEtapa },
): Promise<Etapa> {
  const r = await request<{ data: Etapa }>(`/workflow/etapas/${etapaId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return r.data;
}

export async function borrarEtapa(etapaId: string): Promise<void> {
  await request<{ data: unknown }>(`/workflow/etapas/${etapaId}`, { method: 'DELETE' });
}

export async function anadirRolAEtapa(
  etapaId: string,
  tipoRolGobernanzaDefId: string,
  puestoFiltroId: string | null,
): Promise<EtapaRol> {
  const r = await request<{ data: EtapaRol }>(`/workflow/etapas/${etapaId}/roles`, {
    method: 'POST',
    body: JSON.stringify({ tipoRolGobernanzaDefId, puestoFiltroId }),
  });
  return r.data;
}

export async function quitarRolDeEtapa(etapaRolId: string): Promise<void> {
  await request<{ data: unknown }>(`/workflow/etapa-roles/${etapaRolId}`, { method: 'DELETE' });
}

// =============================================================================
// 0658a — Plantillas notif por etapa (ADR-027.1)
// =============================================================================

export interface NotifPlantillaItem {
  id: string;
  workflowEtapaId: string;
  canal: CanalNotifWorkflow;
  asuntoEs: string;
  asuntoEn: string;
  asuntoZh: string;
  cuerpoEs: string;
  cuerpoEn: string;
  cuerpoZh: string;
  activa: boolean;
  creadoEn: string;
  actualizadoEn: string;
}

export interface NotifPlantillaPayload {
  asuntoEs: string;
  asuntoEn: string;
  asuntoZh: string;
  cuerpoEs: string;
  cuerpoEn: string;
  cuerpoZh: string;
  activa?: boolean;
}

/** Lista las plantillas de una etapa (max 4, una por canal). */
export async function fetchNotifPlantillas(etapaId: string): Promise<NotifPlantillaItem[]> {
  const r = await request<{ data: NotifPlantillaItem[] }>(
    `/workflow/etapas/${etapaId}/notif-plantillas`,
  );
  return r.data;
}

/** Upsert plantilla por (etapa, canal) con paridad i18n ES/EN/ZH. */
export async function upsertNotifPlantilla(
  etapaId: string,
  canal: CanalNotifWorkflow,
  payload: NotifPlantillaPayload,
): Promise<NotifPlantillaItem> {
  const r = await request<{ data: NotifPlantillaItem }>(
    `/workflow/etapas/${etapaId}/notif-plantillas/${canal}`,
    { method: 'PUT', body: JSON.stringify(payload) },
  );
  return r.data;
}

/** Soft-delete: solo borra si plantilla.activa=false. Si activa devuelve 409. */
export async function borrarNotifPlantilla(
  etapaId: string,
  canal: CanalNotifWorkflow,
): Promise<void> {
  await request<{ data: { borrada: boolean } }>(
    `/workflow/etapas/${etapaId}/notif-plantillas/${canal}`,
    { method: 'DELETE' },
  );
}
